import { useTranslation } from '@modernloop/i18n';
import { getDateFnsLocale } from '@modernloop/shared/datetime';
import { MoreIcon, NotificationIcon } from '@modernloop/shared/icons';
import { Avatar, Box, IconButton, ListItemButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { formatDistanceToNow, parseISO } from 'date-fns';

import type { InboxNotificationView } from './types';

type NotificationListItemProps = {
  notification: InboxNotificationView;
  onClick: (notification: InboxNotificationView) => void;
  onToggleRead: (notification: InboxNotificationView) => void;
  onMuteType: (notification: InboxNotificationView) => void;
};

const getInitials = (actorName: string) =>
  actorName
    .split(' ')
    .filter(Boolean)
    .map((name) => name[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

/** Renders a single notification row from a display-ready notification model. */
const NotificationListItem = ({ notification, onClick, onToggleRead, onMuteType }: NotificationListItemProps) => {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const actorName = notification.actorName;
  const actorNameAtStart = actorName !== null && notification.plainText.startsWith(actorName);
  const messageAfterActor = actorNameAtStart ? notification.plainText.slice(actorName.length) : notification.plainText;
  const createdAt = parseISO(notification.createdAt);
  const relativeTime = Number.isNaN(createdAt.getTime())
    ? null
    : formatDistanceToNow(createdAt, { addSuffix: true, locale: getDateFnsLocale() });

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuAction = (action: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    setMenuAnchor(null);
    action();
  };

  return (
    <>
      <ListItemButton
        onClick={() => onClick(notification)}
        sx={{
          alignItems: 'flex-start',
          borderRadius: 0,
          gap: 1.5,
          px: 2,
          py: 1.5,
          bgcolor: notification.isRead ? 'transparent' : 'action.hover',
          '&:hover': { bgcolor: 'action.selected' },
          '&:hover .notification-actions, &:focus-within .notification-actions': { opacity: 1 },
        }}
      >
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          {actorName ? (
            <Avatar alt={actorName} src={notification.actorImageUrl ?? undefined} sx={{ width: 40, height: 40 }}>
              {getInitials(actorName)}
            </Avatar>
          ) : (
            <Avatar sx={{ width: 40, height: 40, bgcolor: 'action.hover', color: 'text.secondary' }}>
              <NotificationIcon fontSize="small" />
            </Avatar>
          )}
          {!notification.isRead && (
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                top: -2,
                right: -2,
                width: 8,
                height: 8,
                border: 2,
                borderColor: 'background.paper',
                borderRadius: '50%',
                bgcolor: 'primary.main',
              }}
            />
          )}
        </Box>
        <Stack flex={1} minWidth={0} gap={0.25}>
          <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
            {actorNameAtStart && (
              <Box component="span" fontWeight={600}>
                {actorName}
              </Box>
            )}
            {messageAfterActor}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {notification.typeLabel}
            {relativeTime && <> • {relativeTime}</>}
          </Typography>
        </Stack>
        <IconButton
          aria-label={t('hiring-dashboard-nav:nav.notificationInbox.actions')}
          className="notification-actions"
          onClick={handleOpenMenu}
          size="small"
          sx={{
            alignSelf: 'center',
            opacity: menuAnchor ? 1 : 0,
            transition: 'opacity 150ms',
            '@media (hover: none)': { opacity: 1 },
          }}
        >
          <MoreIcon sx={{ transform: 'rotate(90deg)' }} />
        </IconButton>
        {!notification.isRead && (
          <>
            {/* An aria-label would replace the row's accessible name, so the unread state is a hidden text suffix instead. */}
            <Box
              component="span"
              sx={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                margin: '-1px',
                padding: 0,
                overflow: 'hidden',
                clipPath: 'inset(50%)',
                whiteSpace: 'nowrap',
              }}
            >
              {t('hiring-dashboard-nav:nav.notificationInbox.unread')}
            </Box>
          </>
        )}
      </ListItemButton>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={handleMenuAction(() => onToggleRead(notification))}>
          {notification.isRead
            ? t('hiring-dashboard-nav:nav.notificationInbox.markUnread')
            : t('hiring-dashboard-nav:nav.notificationInbox.markRead')}
        </MenuItem>
        {notification.notificationType && (
          <MenuItem onClick={handleMenuAction(() => onMuteType(notification))}>
            {t('hiring-dashboard-nav:nav.notificationInbox.turnOffType', { typeLabel: notification.typeLabel })}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default NotificationListItem;
